import { useEffect, useState } from "react";
import {
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Divider,
  Button,
  Grid,
  FormControl,
  FormLabel,
  GridItem,
  VStack,
  Input,
} from "@chakra-ui/react";
import CustomFileInput from "./inputs/CustomFileInput";
import { useMediaQuery } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import AutocompleteInput from "./inputs/AutocompleteInput";
import AutocompleteSelect from "./inputs/AutocompleteSelect";

function ProductDetails({
  productSections,
  setProductSections,
  index,
  userType,
}) {
  const { sav_form } = useIntl().messages;

  const [isMobile] = useMediaQuery("(max-width: 480px)");

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [finalProduct, setFinalProduct] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);

  const addProductSection = () => {
    setProductSections(productSections + 1);
  };

  const removeProductSection = () => {
    if (productSections > 1) {
      setProductSections(productSections - 1);
    }
  };

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_SEARCH_API_BASE_URL;
    const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

    if (!selectedProduct || !selectedColor || !selectedSize) {
      return;
    }

    const fetchProduct = async () => {
      try {
        const response = await fetch(`${baseUrl}/products/search`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "/*/",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            q: selectedProduct.CODE_ARTICLE,
            filter: [
              `LIB_COLORIS = "${selectedColor}"`,
              `TAILLE = "${selectedSize}"`,
            ],
            limit: 1,
          }),
        });
        const data = await response.json();
        const { hits } = data;
        setFinalProduct(hits[0]);
      } catch (error) {
        console.error("Error fetching product: ", error);
      }
    };

    fetchProduct();
  }, [selectedProduct, selectedColor, selectedSize]);

  return (
    <Box key={index}>
      {index > 0 && <Divider mt={6} mb={6} />}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <h3>
          {sav_form.product_section} {index + 1}
        </h3>
        {productSections > 1 && (
          <Button
            type="button"
            variant="secondary"
            height="auto"
            mb="4"
            p="4"
            onClick={removeProductSection}
          >
            {sav_form.remove_product}
          </Button>
        )}
      </div>

      <Grid
        templateColumns={{
          sm: "repeat(2, 1fr)",
        }}
        gap={5}
        width="100%"
      >
        <GridItem colSpan={2}>
          <AutocompleteInput
            name={`productLabel_${index + 1}`}
            label={sav_form.product}
            placeholder={sav_form.product}
            apiIndex={"products"}
            selectedItem={selectedProduct}
            setSelectedItem={(item) => {
              setSelectedProduct(item);
              if (item) setIsInvalid(false);
            }}
            itemLabel={(item) => `${item.LIB_ARTICLE} - ${item.CODE_ARTICLE}`}
            fetchParams={{
              limit: 100,
              distinct: "CODE_ARTICLE",
            }}
            inputFilter={false}
            isRequired
            isInvalid={isInvalid}
            errorText={sav_form.product_error}
            pattern="^(.+) - (.+)$"
            onBlur={() => {
              if (!selectedProduct) {
                setIsInvalid(true);
              }
            }}
          />
        </GridItem>
      </Grid>
      {selectedProduct && (
        <>
          <input
            type="hidden"
            name={`productName_${index + 1}`}
            value={selectedProduct.LIB_ARTICLE}
          />
          <input
            type="hidden"
            name={`productReference_${index + 1}`}
            value={selectedProduct.CODE_ARTICLE}
          />
        </>
      )}
      {selectedProduct && (
        <>
          <Grid
            templateColumns={{
              sm: "repeat(3, 1fr)",
            }}
            gap={5}
            mt={5}
            width="100%"
          >
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel htmlFor={`productQuantity_${index + 1}`}>
                  {sav_form.product_quantity}
                </FormLabel>
                <NumberInput
                  defaultValue={1}
                  min={1}
                  max={10}
                  id={`productQuantity_${index + 1}`}
                  name={`productQuantity_${index + 1}`}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <AutocompleteSelect
                name={`productSize_${index + 1}`}
                label={sav_form.product_size.label}
                placeholder={sav_form.product_size.placeholder}
                apiIndex={"products"}
                fetchParams={{
                  filter: `CODE_ARTICLE = ${selectedProduct.CODE_ARTICLE}`,
                  limit: 30,
                  distinct: "TAILLE",
                }}
                selectedItem={selectedSize}
                setSelectedItem={setSelectedSize}
                itemLabel={(item) => item.TAILLE}
                valuesToSelect={(item) => item.TAILLE}
                isRequired
              />
            </GridItem>
            {selectedSize && (
              <GridItem colSpan={1}>
                <AutocompleteSelect
                  name={`productColor_${index + 1}`}
                  label={sav_form.product_color.label}
                  placeholder={sav_form.product_color.placeholder}
                  apiIndex={"products"}
                  fetchParams={{
                    filter: [
                      `CODE_ARTICLE = ${selectedProduct.CODE_ARTICLE}`,
                      `TAILLE = "${selectedSize}"`,
                    ],
                    limit: 30,
                    distinct: "LIB_COLORIS",
                  }}
                  selectedItem={selectedColor}
                  setSelectedItem={setSelectedColor}
                  itemLabel={(item) => item.LIB_COLORIS}
                  valuesToSelect={(item) => item.LIB_COLORIS}
                />
              </GridItem>
            )}
            {finalProduct && userType === "business" && (
              <GridItem colSpan={3}>
                <FormControl>
                  <FormLabel htmlFor={`productEAN_${index + 1}`}>EAN</FormLabel>
                  <Input readOnly value={selectedProduct.EAN} />
                </FormControl>
              </GridItem>
            )}
          </Grid>

          {userType === "individual" && (
            <input
              type="hidden"
              name={`productEAN_${index + 1}`}
              value={selectedProduct.EAN}
            />
          )}
        </>
      )}

      <VStack spacing={5} mt={5}>
        <FormControl isRequired>
          <FormLabel htmlFor={`problemDescription_${index + 1}`}>
            {sav_form.problem_description.label}
          </FormLabel>
          <Textarea
            id={`problemDescription_${index + 1}`}
            name={`problemDescription_${index + 1}`}
            placeholder={sav_form.problem_description.placeholder}
            size="sm"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel htmlFor={`productPhoto_${index + 1}`}>
            {sav_form.product_photo.label}
          </FormLabel>
          <CustomFileInput
            id={`productPhoto_${index + 1}`}
            name={`productPhoto_${index + 1}`}
            placeholder={sav_form.product_photo.placeholder}
            required
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel htmlFor={`defectPhoto_${index + 1}`}>
            {sav_form.defect.label}
          </FormLabel>
          <CustomFileInput
            id={`defectPhoto_${index + 1}`}
            name={`defectPhoto_${index + 1}`}
            placeholder={sav_form.defect.placeholder}
            required
          />
        </FormControl>

        <Grid
          templateColumns={{
            base: "1fr",
          }}
          gap={5}
          width="100%"
          justifyItems={isMobile ? "stretch" : "end"}
        >
          {index === productSections - 1 && (
            <Button
              type="button"
              height="auto"
              variant="primary"
              mb="4"
              p="4"
              onClick={addProductSection}
              width={isMobile ? "100%" : "auto"}
            >
              {sav_form.add_product}
            </Button>
          )}
        </Grid>
      </VStack>
    </Box>
  );
}

export default ProductDetails;
